import { BLEND_MODES, Container, SCALE_MODES, Sprite, Texture } from "pixi.js"
import { Entity, LightsContainer } from "game-common/models"

/**
 * Short-lived light: the flash of an arrow leaving the bow, a body breaking apart, the dead climbing
 * out of the ground when a wave lands.
 *
 * TWO HALVES, because the world has two ways of being lit.
 *
 * 1. A GLOW in the world surface: an additive bloom with a hot core, over a small normal-blend HEART
 *    in the flash's own colour. The heart is what keeps the colour by day: additive light can only
 *    brighten, and over green grass a violet or blue bloom brightens to mint whatever its hue (the
 *    filmstrip showed it). Two layers, one per blend mode, so the whole pool is two batched draws
 *    however many flashes are going. It sits above every body (a flash lights the people standing
 *    next to it) and below the fog and the roofs, at LavaEmbers' depth for the same reasons.
 * 2. A LIGHT in the night/cave mask (shadow_rendering.ts): an entry in the entity manager's
 *    LightsContainer for as long as the flash lasts, so after dark it actually lifts the darkness
 *    around itself -- the archer is lit for an instant, the crumble is seen falling, the grave lights
 *    at the edge of camp show where the wave is coming from. Only while the room is dark: by day the
 *    mask is not drawn at all and an entry would only churn its change stamp.
 *
 * WHAT IT COSTS. The glow is up to three sprites per flash over ONE baked texture, so the whole pool
 * batches into a single draw. The mask half re-bakes the mask every frame a flash is alive, because a
 * changing light is a changing mask -- but that is exactly what walking already costs (the player's
 * position is in the mask's hash), and a flash lives well under a second. MAX_MASK_LIGHTS bounds the
 * cave case, where each light is shaped against the rock.
 *
 * DRIVEN OFF THE RENDER LOOP (EntityRenderingManager.endFrame), never a timer, and timed off the clock
 * rather than summed deltas: a backgrounded tab that comes back finds every flash it missed already
 * over, instead of replaying them.
 *
 * LIVE TUNING, from the browser console -- no reload:
 *   window.transientLights = false   turn the whole effect off
 *   window.flashGain = 1.0           scale every glow
 *   window.flashDayGain = 0.65       how much of the night glow survives in full daylight
 *   window.flashTest("kill")         fire one beside the player (see EntityRenderingManager)
 */

export type FlashKind = "arrow" | "kill" | "graveKill" | "fallen" | "graveRise"

export interface FlashPreset {
    /**
     * Additive bloom colour. Starved of green, every one: most of the world is grass, and green in an
     * additive light only adds to the green already there -- orange lands on yellow-lime, violet on
     * mint, blue on teal (measured on a filmstrip over the real grass tiles).
     */
    glow: number
    /** The hot centre, which is allowed near white because it is small and goes first. */
    core: number
    glowRadius: number
    coreRadius: number
    glowAlpha: number
    coreAlpha: number
    /** Up to the peak. 0 is a strike; a slow attack is light leaking out of something. */
    attackMs: number
    /** Peak down to nothing. */
    decayMs: number
    /** The core's own tail, shorter than the glow's: the white goes first and the colour lingers. */
    coreDecayMs: number
    /** The hole it punches in the night mask, at the peak. */
    lightRadius: number
    lightIntensity: number
    /** How far the light drifts up over its life, in world px -- a soul or an ember lifting off. */
    rise: number
    /** The glow's height over its width. Below 1 it lies on the ground. */
    squash: number
    /** 0..1: how much the light gutters while it builds. */
    flicker: number
    /** The normal-blend heart's peak; HEART_ALPHA unless a flash should have no solid centre. */
    heartAlpha?: number
    /** A stretched glow along a heading: the arrow's release, the shaft out of a grave. */
    streak?: { length: number; width: number; alpha: number; angle?: number }
}

export const FLASH_PRESETS: Record<FlashKind, FlashPreset> = {
    // The bow's release: a faint glint at the grip and a whisper of a streak along the shot, gone in
    // a tenth of a second. A bow is not a gun -- the first version (a hot warm dot, a lit archer at
    // night) read as a muzzle flash. So no heart, no light in the dark, and barely any glow: it is
    // felt as the string snapping, not seen as a flash.
    arrow: {
        glow: 0xff5a10, core: 0xfff0d0, glowRadius: 9, coreRadius: 3, glowAlpha: 0.16, coreAlpha: 0.3,
        attackMs: 0, decayMs: 110, coreDecayMs: 50, lightRadius: 0, lightIntensity: 0,
        rise: 0, squash: 1, flicker: 0, heartAlpha: 0,
        streak: { length: 20, width: 0.2, alpha: 0.22 },
    },
    // A beast or a bandit going down: an ember-coloured burst that lifts as it dies.
    //
    // The three deaths run at HALF their first brightness (glow, core, heart and the light in the
    // dark alike): at full strength they out-shouted the fight -- a kill should register, not flash.
    kill: {
        glow: 0xff3d08, core: 0xffc890, glowRadius: 46, coreRadius: 13, glowAlpha: 0.38, coreAlpha: 0.48,
        attackMs: 40, decayMs: 520, coreDecayMs: 180, lightRadius: 84, lightIntensity: 0.43,
        rise: 10, squash: 1, flicker: 0, heartAlpha: 0.4,
    },
    // The dead going back: the same grave light they rose in (see graveRise). The Bonecaller's
    // violet, with NO green in it at all -- added to grass, any green turns it lavender-white, and
    // the venom green the shrine uses vanishes into the grass outright.
    graveKill: {
        glow: 0xb000ff, core: 0xf0d8ff, glowRadius: 50, coreRadius: 12, glowAlpha: 0.4, coreAlpha: 0.45,
        attackMs: 50, decayMs: 640, coreDecayMs: 220, lightRadius: 88, lightIntensity: 0.43,
        rise: 16, squash: 1, flicker: 0, heartAlpha: 0.4,
    },
    // One of ours: cold, slower in and much slower out, lifting further. A light going out, not a hit.
    fallen: {
        glow: 0x2048ff, core: 0xe0ecff, glowRadius: 54, coreRadius: 14, glowAlpha: 0.3, coreAlpha: 0.43,
        attackMs: 140, decayMs: 1200, coreDecayMs: 420, lightRadius: 96, lightIntensity: 0.4,
        rise: 22, squash: 1, flicker: 0, heartAlpha: 0.4,
    },
    // The ground opening under a rising skeleton or zombie. Builds while the body climbs (the attack
    // is set per spawn from the rupture's own clock), guttering, flat on the ground, with a shaft of
    // light standing up out of the hole.
    graveRise: {
        glow: 0xa000ff, core: 0xe8c8ff, glowRadius: 40, coreRadius: 12, glowAlpha: 0.8, coreAlpha: 0.8,
        attackMs: 540, decayMs: 650, coreDecayMs: 260, lightRadius: 72, lightIntensity: 0.7,
        rise: 0, squash: 0.5, flicker: 0.35,
        streak: { length: 34, width: 0.42, alpha: 0.7, angle: -Math.PI / 2 },
    },
}

/**
 * Brightness 0..1 at `ageMs` into a flash. Smoothstep up to the peak, quadratic down: fast in, slow
 * out, the same shape as the wave landing's own hit. Exported pure, like the other effects' curves.
 */
export const flashEnvelope = (ageMs: number, attackMs: number, decayMs: number): number => {
    if (!(ageMs >= 0)) return 0
    if (ageMs < attackMs) {
        const t = ageMs / attackMs
        return t * t * (3 - 2 * t)
    }
    const t = decayMs > 0 ? (ageMs - attackMs) / decayMs : 1
    return t >= 1 ? 0 : (1 - t) * (1 - t)
}

// The dead that rise out of the ground (their population profile's preSpawnEffect is a
// ground-rupture) go back into it in the same light.
const GRAVE_RACES = new Set(["skeleton", "zombie"])
// Things a fraction of a person's size: a full-sized burst over a rabbit is a bomb going off.
const SMALL_RACES = new Set(["rabbit", "chicken", "snake", "slime", "redspider"])

/** Which flash a death gets, or undefined for things that are not creatures (chests and the like). */
export const deathFlash = (entity: Entity, ally: boolean): { kind: FlashKind; scale: number } | undefined => {
    const race = entity.appearance?.race
    if (entity.entityType !== "player" && !race) return undefined
    const size = Math.max(0.5, Math.min(1.6, entity.spriteScale || 1))
    if (ally || entity.entityType === "player") return { kind: "fallen", scale: size }
    if (GRAVE_RACES.has(race)) return { kind: "graveKill", scale: size }
    return { kind: "kill", scale: (SMALL_RACES.has(race) ? 0.6 : 1) * size }
}

// The glow texture: a soft radial falloff, white so every flash tints it. Gaussian-ish rather than a
// cone so the bloom has no visible edge or point, forced to zero at the rim so the square never shows.
const TEXTURE_SIZE = 64
const TEXTURE_RADIUS = TEXTURE_SIZE / 2

export const flashTexturePixels = (): Uint8Array => {
    const rgba = new Uint8Array(TEXTURE_SIZE * TEXTURE_SIZE * 4)
    for (let y = 0; y < TEXTURE_SIZE; y++) {
        for (let x = 0; x < TEXTURE_SIZE; x++) {
            const dx = (x + 0.5 - TEXTURE_RADIUS) / TEXTURE_RADIUS
            const dy = (y + 0.5 - TEXTURE_RADIUS) / TEXTURE_RADIUS
            const r = Math.sqrt(dx * dx + dy * dy)
            const edge = r >= 1 ? 0 : r <= 0.8 ? 1 : 1 - (r - 0.8) / 0.2
            const a = Math.exp(-r * r * 4) * edge
            const i = (y * TEXTURE_SIZE + x) * 4
            rgba[i] = rgba[i + 1] = rgba[i + 2] = 255
            rgba[i + 3] = Math.round(a * 255)
        }
    }
    return rgba
}

let sharedTexture: Texture | undefined
const flashTexture = (): Texture => {
    if (!sharedTexture) {
        sharedTexture = Texture.fromBuffer(flashTexturePixels(), TEXTURE_SIZE, TEXTURE_SIZE,
            { scaleMode: SCALE_MODES.LINEAR })
    }
    return sharedTexture
}

interface Flash {
    live: boolean
    preset: FlashPreset
    x: number
    y: number
    bornMs: number
    attackMs: number
    scale: number
    angle: number
    seed: number
    heart: Sprite
    glow: Sprite
    core: Sprite
    streak: Sprite
}

export interface FlashOptions {
    /** Size multiplier: radii and the mask hole scale with it. */
    scale?: number
    /** The streak's heading, for presets that have one and do not fix it themselves. */
    angle?: number
    /** Override the preset's attack -- a grave light builds for as long as its rupture runs. */
    attackMs?: number
}

const LIGHT_ID_PREFIX = "flash:"

// The normal-blend heart, against the core's radius and peak. Small and short (it lives on the core's
// clock): any bigger or longer and an opaque disc reads as a sticker on the ground, not as light.
const HEART_SIZE = 1.1
const HEART_ALPHA = 0.8

const option = (key: string, fallback: number): number => {
    const value = typeof window === "undefined" ? undefined : (window as any)[key]
    return typeof value === "number" && Number.isFinite(value) ? value : fallback
}

export class TransientLights {
    static readonly Z_INDEX = 880001
    // A wave's worth of risings plus the fight around them. Past this the oldest flash is recycled,
    // which near the end of its tail nobody sees go.
    static readonly MAX_FLASHES = 24
    // Lights the mask carries at once. In a cave each one is shaped against the rock.
    static readonly MAX_MASK_LIGHTS = 8
    static readonly DAY_GAIN = 0.65

    readonly container = new Container()
    // Exposed for tests. Hearts under the light, so the bloom washes over them.
    readonly heartLayer = new Container()
    readonly glowLayer = new Container()
    private flashes: Flash[] = []
    private registered = new Set<number>()
    private roomId: string | undefined

    constructor(private lights: LightsContainer, private makeTexture: () => Texture = flashTexture) {
        this.container.zIndex = TransientLights.Z_INDEX
        this.container.interactiveChildren = false
        this.container.visible = false
        this.container.addChild(this.heartLayer, this.glowLayer)
    }

    /** Light one. `x`/`y` are in the world surface's space -- the same space as the mask's lights. */
    flash = (kind: FlashKind, x: number, y: number, opts: FlashOptions = {}, nowMs: number = performance.now()) => {
        if (!this.enabled() || !Number.isFinite(x) || !Number.isFinite(y)) return
        const preset = FLASH_PRESETS[kind]
        const flash = this.take(nowMs)
        flash.live = true
        flash.preset = preset
        flash.x = x
        flash.y = y
        flash.bornMs = nowMs
        flash.attackMs = Math.max(0, opts.attackMs ?? preset.attackMs)
        flash.scale = Math.max(0.1, opts.scale ?? 1)
        flash.angle = preset.streak?.angle ?? opts.angle ?? 0
        flash.seed = Math.random() * 1000
        flash.glow.tint = preset.glow
        flash.core.tint = preset.core
        flash.heart.tint = preset.core
        flash.streak.tint = preset.glow
        this.container.visible = true
    }

    /**
     * Advance every live flash and keep the mask's entries in step. `ambient` is the room's
     * visibility darkness as the mask reads it: 1 (or more) is broad day with no mask at all.
     */
    update = (ambient: number, roomId: string | undefined, nowMs: number = performance.now()) => {
        if (roomId !== this.roomId) {
            // The first room seen is not a change: anything lit before it belongs to it.
            if (this.roomId !== undefined) this.clear()
            this.roomId = roomId
        }
        if (!this.enabled()) {
            this.clear()
            return
        }
        const light = Number.isFinite(ambient) ? Math.max(0, Math.min(1, ambient)) : 1
        const dark = light < 1
        const gain = option("flashGain", 1) * (1 + (option("flashDayGain", TransientLights.DAY_GAIN) - 1) * light)
        let anyLive = false
        let maskLights = 0
        for (let i = 0; i < this.flashes.length; i++) {
            const flash = this.flashes[i]
            if (!flash.live) continue
            const age = nowMs - flash.bornMs
            const p = flash.preset
            const level = flashEnvelope(age, flash.attackMs, p.decayMs)
            if (level <= 0 && age > 0) {
                this.retire(i)
                continue
            }
            anyLive = true
            const building = age < flash.attackMs
            const gutter = building && p.flicker > 0
                ? 1 - p.flicker * (0.5 + 0.5 * Math.sin(age * 0.031 + flash.seed) * Math.sin(age * 0.053 + flash.seed * 1.7))
                : 1
            const power = level * gutter
            const total = flash.attackMs + p.decayMs
            const y = flash.y - p.rise * flash.scale * Math.min(1, total > 0 ? age / total : 1)
            const coreLevel = flashEnvelope(age, flash.attackMs, p.coreDecayMs) * gutter

            const glowScale = (p.glowRadius * flash.scale) / TEXTURE_RADIUS
            flash.glow.position.set(flash.x, y)
            flash.glow.scale.set(glowScale, glowScale * p.squash)
            flash.glow.alpha = Math.min(1, p.glowAlpha * power * gain)

            const coreScale = (p.coreRadius * flash.scale) / TEXTURE_RADIUS
            flash.core.position.set(flash.x, y)
            flash.core.scale.set(coreScale, coreScale)
            flash.core.alpha = Math.min(1, p.coreAlpha * coreLevel * gain)
            flash.core.visible = flash.core.alpha > 0.004

            // The heart keeps its strength by day -- daylight is exactly when it is needed.
            flash.heart.position.set(flash.x, y)
            flash.heart.scale.set(coreScale * HEART_SIZE, coreScale * HEART_SIZE)
            flash.heart.alpha = Math.min(1, (p.heartAlpha ?? HEART_ALPHA) * coreLevel * option("flashGain", 1))
            flash.heart.visible = flash.heart.alpha > 0.004

            if (p.streak) {
                const length = p.streak.length * flash.scale
                const cos = Math.cos(flash.angle), sin = Math.sin(flash.angle)
                flash.streak.visible = true
                flash.streak.rotation = flash.angle
                flash.streak.position.set(flash.x + cos * length * 0.5, y + sin * length * 0.5)
                flash.streak.scale.set(length / TEXTURE_SIZE, p.streak.width * length / TEXTURE_SIZE)
                flash.streak.alpha = Math.min(1, p.streak.alpha * power * gain)
            } else {
                flash.streak.visible = false
            }

            if (dark && maskLights < TransientLights.MAX_MASK_LIGHTS && p.lightIntensity * power > 0.01) {
                maskLights++
                this.registered.add(i)
                this.lights.set(LIGHT_ID_PREFIX + i, {
                    entityId: LIGHT_ID_PREFIX + i,
                    location: { x: flash.x, y },
                    radius: p.lightRadius * flash.scale * (0.7 + 0.3 * level),
                    intensity: Math.min(1, p.lightIntensity * power),
                })
            } else {
                this.unregister(i)
            }
        }
        this.container.visible = anyLive
    }

    /** Put every flash out now and take its light out of the mask: a room change, or switched off. */
    clear = () => {
        for (let i = 0; i < this.flashes.length; i++) {
            if (this.flashes[i].live) this.retire(i)
        }
        this.container.visible = false
    }

    // Exposed for tests.
    liveCount = (): number => this.flashes.filter(f => f.live).length
    maskLightCount = (): number => this.registered.size

    private enabled = () => typeof window === "undefined" || (window as any).transientLights !== false

    private take = (nowMs: number): Flash => {
        const free = this.flashes.find(f => !f.live)
        if (free) return free
        if (this.flashes.length < TransientLights.MAX_FLASHES) {
            const texture = this.makeTexture()
            const sprite = (layer: Container, blendMode: BLEND_MODES) => {
                const s = new Sprite(texture)
                s.anchor.set(0.5)
                s.alpha = 0
                s.blendMode = blendMode
                layer.addChild(s)
                return s
            }
            const add = () => sprite(this.glowLayer, BLEND_MODES.ADD)
            const flash: Flash = {
                live: false, preset: FLASH_PRESETS.kill, x: 0, y: 0, bornMs: 0, attackMs: 0, scale: 1, angle: 0,
                seed: 0, heart: sprite(this.heartLayer, BLEND_MODES.NORMAL), glow: add(), core: add(), streak: add(),
            }
            this.flashes.push(flash)
            return flash
        }
        // Full: recycle whichever is furthest through its life.
        let oldest = this.flashes[0]
        let oldestProgress = -Infinity
        for (const flash of this.flashes) {
            const progress = (nowMs - flash.bornMs) / Math.max(1, flash.attackMs + flash.preset.decayMs)
            if (progress > oldestProgress) {
                oldestProgress = progress
                oldest = flash
            }
        }
        return oldest
    }

    private retire = (i: number) => {
        const flash = this.flashes[i]
        flash.live = false
        flash.heart.alpha = flash.glow.alpha = flash.core.alpha = flash.streak.alpha = 0
        this.unregister(i)
    }

    private unregister = (i: number) => {
        if (!this.registered.delete(i)) return
        this.lights.remove(LIGHT_ID_PREFIX + i)
    }
}
